import React, { useCallback, useMemo, useState } from "react";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RouteProp } from "@react-navigation/native";
import { StyleSheet, View } from "react-native";
import * as Sentry from "@sentry/react-native";
import LTILayout from "../layouts/LTILayout";
import InteractiveLearningLTIFormContainer from "../containers/customCoursework/InteractiveLearningForm/InteractiveLearningLTIFormContainer";
import type { LTIErrorCode, RootStackParamList } from "../navigation/navigation.types";
import { getAuthHeader } from "../utils/authRefresh";
import { useAppConfig } from "../AppConfigProvider";
import { useAuth } from "../hooks/useAuth";
import Loader from "../components/common/status/Loader/Loader";
import DeepLinkAutoSubmit from "../components/lti/DeepLinkAutoSubmit";
import LTIErrorView from "./LTIErrorView";

type DeepLinkCompleteResponse = {
  jwt?: string;
  deepLinkReturnUrl?: string;
  error?: string;
};

type DeepLinkMessagePayload = {
  type: "AXIO_LTI_DEEP_LINK_ILE_SUCCESS" | "AXIO_LTI_DEEP_LINK_ILE_ERROR";
  ileId?: string;
  title?: string;
  error?: string;
  integrationId?: string;
};

function LTIDeepLinkILEView() {
  const route = useRoute<RouteProp<RootStackParamList, "LTIDeepLink">>();
  const routeParams = route.params;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { login } = useAuth();
  const appConfig = useAppConfig();
  const currentDomain = appConfig?.currentDomain;
  const imageGenerationSupportFlag = appConfig?.imageGenerationSupportFlag;
  const [submitError, setSubmitError] = useState<{ code: LTIErrorCode; message: string } | null>(null);
  const [isBootstrappingAuth, setIsBootstrappingAuth] = useState(true);
  const [isCompletingDeepLink, setIsCompletingDeepLink] = useState(false);
  const [deepLinkResponse, setDeepLinkResponse] = useState<{ jwt: string; deepLinkReturnUrl: string } | null>(null);

  const searchParams = useMemo(() => {
    if (typeof window === "undefined") {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }, []);

  const accessToken = routeParams?.accessToken ?? searchParams.get("accessToken") ?? undefined;
  const refreshToken = routeParams?.refreshToken ?? searchParams.get("refreshToken") ?? undefined;
  const deepLinkStateToken = routeParams?.deepLinkStateToken ?? searchParams.get("deepLinkStateToken") ?? undefined;
  const integrationId = routeParams?.integrationId ?? searchParams.get("integrationId") ?? undefined;

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const bootstrapAuth = async () => {
        if (!deepLinkStateToken) {
          if (isActive) {
            setSubmitError({
              code: "invalid_launch",
              message: "Missing deep-link launch context.",
            });
            setIsBootstrappingAuth(false);
          }
          return;
        }

        if (!accessToken || !refreshToken) {
          if (isActive) {
            setIsBootstrappingAuth(false);
          }
          return;
        }

        try {
          const authResult = await login(accessToken, refreshToken);
          if (!isActive) {
            return;
          }

          const isVerified = authResult.isEmailVerified && authResult.otpVerifiedForSession;
          if (!isVerified) {
            navigation.reset({
              index: 0,
              routes: [{ name: "Verify Email" }],
            });
            return;
          }

          setIsBootstrappingAuth(false);
        } catch {
          if (!isActive) {
            return;
          }
          setSubmitError({ code: "auth_failed", message: "LTI authentication failed" });
          setIsBootstrappingAuth(false);
        }
      };

      bootstrapAuth();

      return () => {
        isActive = false;
      };
    }, [accessToken, deepLinkStateToken, refreshToken, login, navigation])
  );

  const postToParent = (payload: DeepLinkMessagePayload) => {
    if (typeof window === "undefined") {
      return;
    }

    if (!window.parent || window.parent === window) {
      return;
    }

    window.parent.postMessage(payload, window.location.origin);
  };

  const handleSuccess = async (ileId: string, title: string) => {
    setSubmitError(null);

    if (!deepLinkStateToken) {
      setSubmitError({
        code: "invalid_launch",
        message: "Missing deepLinkStateToken parameter.",
      });
      return;
    }

    setIsCompletingDeepLink(true);

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch("/auth/lti/deep-link-complete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
        credentials: "include",
        body: JSON.stringify({
          aitutorId: ileId,
          title,
          deepLinkStateToken,
        }),
      });

      const payload = (await response.json().catch(() => null)) as DeepLinkCompleteResponse | null;

      if (!response.ok) {
        throw new Error(payload?.error || "Failed to complete LTI deep link");
      }

      if (!payload?.jwt || !payload?.deepLinkReturnUrl) {
        throw new Error("Deep link completion response was missing required data");
      }

      postToParent({
        type: "AXIO_LTI_DEEP_LINK_ILE_SUCCESS",
        ileId,
        title,
        integrationId,
      });

      setDeepLinkResponse({
        jwt: payload.jwt,
        deepLinkReturnUrl: payload.deepLinkReturnUrl,
      });
    } catch (error) {
      Sentry.captureException(error);
      const errorMessage = error instanceof Error ? error.message : "Failed to complete LTI deep link";
      setSubmitError({ code: "unknown", message: errorMessage });

      postToParent({
        type: "AXIO_LTI_DEEP_LINK_ILE_ERROR",
        error: errorMessage,
        integrationId,
      });
    } finally {
      setIsCompletingDeepLink(false);
    }
  };

  const handleError = (error: Error) => {
    const errorMessage = error.message || "Failed to create interactive learning experience";
    setSubmitError({ code: "unknown", message: errorMessage });

    postToParent({
      type: "AXIO_LTI_DEEP_LINK_ILE_ERROR",
      error: errorMessage,
      integrationId,
    });
  };

  if (!isBootstrappingAuth && submitError) {
    return <LTIErrorView error={submitError.code} message={submitError.message} />;
  }

  if (deepLinkResponse) {
    return (
      <LTILayout>
        <DeepLinkAutoSubmit jwt={deepLinkResponse.jwt} deepLinkReturnUrl={deepLinkResponse.deepLinkReturnUrl} />
      </LTILayout>
    );
  }

  return (
    <LTILayout>
      <View style={styles.container}>
        {isBootstrappingAuth || isCompletingDeepLink ? (
          <View style={styles.loaderWrap}>
            <Loader />
          </View>
        ) : null}

        {!isBootstrappingAuth && !isCompletingDeepLink ? (
          <InteractiveLearningLTIFormContainer
            onSuccess={handleSuccess}
            onError={handleError}
            currentDomain={currentDomain}
            imageGenerationSupportFlag={imageGenerationSupportFlag}
          />
        ) : null}
      </View>
    </LTILayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  loaderWrap: {
    marginTop: 24,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default LTIDeepLinkILEView;
